import type { ReactNode } from "react";
import { useFormContext } from "react-hook-form";
import { useRouter } from "next/router";

import { analytics } from "@flare/analytics";
import { useTranslation } from "@flare/i18n";
import { Button, Icon, IconButton } from "@flare/ui/components";
import { cn } from "@flare/ui/utils";

import type { Steps } from "@/modules/v2/funnel/config";
import { useFunnelAnswers } from "@/modules/v2/funnel/hooks/useFunnelAnswers";
import { BiEventsNames } from "@/services/analytics/bi-event-names";
import { isDirectCampaignTraffic } from "@/services/store/campaign-data-context/campaign-data-utils";

import { useFunnelExperimentContext } from "../funnel-experiment-context";
import type { PracticeList } from "../steps/step.types";
import type { StepHandlers } from "../steps/step.types";

export type ActionsContainerProps = {
  children: ReactNode;
  className?: string;
} & Partial<StepHandlers>;

export function ActionsContainer({
  children,
  className,
  onSkip,
}: ActionsContainerProps) {
  const router = useRouter();
  const { query } = router;
  const { t } = useTranslation();

  const funnelExperimentContext = useFunnelExperimentContext();

  const practice = query?.practice as PracticeList;
  const step = query?.step as Steps;

  const {
    formState: { isSubmitting },
  } = useFormContext();

  const isDirectTraffic = isDirectCampaignTraffic(query);
  const { funnelAnswers } = useFunnelAnswers();
  const data = funnelAnswers ? funnelAnswers : {};

  function handleBackClick() {
    analytics.track(BiEventsNames.WebFunnelClick, {
      practice,
      current_step_key: step,
      answer_value: Object.entries(data).flat().toString(),
      full_answer: JSON.stringify(data),
      click_type: "back",
      click_text: "back_button",
    });
    router.back();
  }

  const showSkipButton = Boolean(onSkip);

  const isIntakeContactInfo =
    step?.includes("contact-info") && step !== "contact-info-dlss";

  if (funnelExperimentContext.showNewQuestionsAndButtons()) {
    return (
      <div className="sticky bottom-sp-0 border-t border-(--color-outline-primary) bg-surface">
        <div className="mx-auto flex w-full max-w-[520px] justify-between bg-surface px-sp-5 py-sp-4 md:px-sp-0">
          <IconButton
            disabled={isSubmitting}
            onClick={handleBackClick}
            type="button"
            variant="extra"
            className={isDirectTraffic ? "invisible" : ""}
          >
            <Icon name="chevron-left" />
          </IconButton>
          <div className="flex gap-sp-2">
            {showSkipButton && (
              <Button disabled={isSubmitting} variant="extra">
                {t("FUNNEL_SKIP_BUTTON")}
              </Button>
            )}
            <Button isLoading={isSubmitting} type="submit">
              {isIntakeContactInfo
                ? t("FUNNEL_CONTACT_INFO_ACTION_BUTTON_NEXT")
                : t("FUNNEL_ACTION_BUTTON_NEXT")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return <div className={cn("text-center", className)}>{children}</div>;
}
